/**
 * Lazy video for mega-menu panel cards.
 *
 * Core Web Vitals contract:
 *  - Nothing is fetched on page load. The <video> carries no src and no poster
 *    until the panel is actually opened, so the header video can never become
 *    the LCP element and never competes for bandwidth with the hero image.
 *  - IMPORTANT: a closed mega panel is `visibility: hidden`, NOT `display: none`,
 *    so it still has a layout box. An IntersectionObserver alone would therefore
 *    report it as visible on load and download the video immediately. The real
 *    trigger is the menu item being hovered/focused; the observer is only a
 *    fallback and is gated on genuine visibility.
 *  - The poster is applied first and the media box has a fixed aspect ratio, so
 *    revealing it cannot shift layout (CLS stays 0).
 *  - Opening costs one attribute write plus play(); decoding is off the main
 *    thread, so it does not inflate INP. Playback pauses when the panel closes
 *    and when the tab is hidden.
 */
const RESOLVED = new WeakSet();

/** @type {IntersectionObserver | null} */
let observer = null;

function getObserver() {
  if (observer) return observer;
  observer = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      const element = /** @type {MegaPanelVideo} */ (entry.target);
      if (entry.isIntersecting) element.activateIfVisible();
      else element.deactivate();
    }
  }, { threshold: 0.01 });
  return observer;
}

class MegaPanelVideo extends HTMLElement {
  /** @type {Element | null} */
  #trigger = null;

  /** @type {Element | null} */
  #nav = null;

  connectedCallback() {
    // The top-level menu item that owns this panel: hovering it is what opens the
    // panel, so it is the honest signal of intent.
    this.#trigger =
      this.closest('.menu-list__submenu')?.parentElement ?? this.closest('li') ?? null;

    if (this.#trigger) {
      this.#trigger.addEventListener('pointerenter', this.#onOpen);
      this.#trigger.addEventListener('focusin', this.#onOpen);
      this.#trigger.addEventListener('click', this.#onOpen);
    }

    // Structure-agnostic backstop: any pointer/focus activity in the nav makes us
    // re-check on the next frame, but we only load once genuinely visible.
    this.#nav = this.closest('nav') ?? this.closest('header');
    this.#nav?.addEventListener('pointerover', this.#onNavActivity, { passive: true });
    this.#nav?.addEventListener('focusin', this.#onNavActivity);

    // Fallback for the mobile drawer and any programmatic reveal.
    getObserver().observe(this);
    document.addEventListener('visibilitychange', this.#onTabHidden);
  }

  disconnectedCallback() {
    this.#trigger?.removeEventListener('pointerenter', this.#onOpen);
    this.#trigger?.removeEventListener('focusin', this.#onOpen);
    this.#trigger?.removeEventListener('click', this.#onOpen);
    this.#nav?.removeEventListener('pointerover', this.#onNavActivity);
    this.#nav?.removeEventListener('focusin', this.#onNavActivity);
    observer?.unobserve(this);
    document.removeEventListener('visibilitychange', this.#onTabHidden);
  }

  #onNavActivity = () => {
    if (RESOLVED.has(this.video ?? {})) return;
    requestAnimationFrame(() => this.activateIfVisible());
  };

  /** @returns {HTMLVideoElement | null} */
  get video() {
    return this.querySelector('video');
  }

  #onOpen = () => this.activate();

  #onTabHidden = () => {
    if (document.hidden) this.video?.pause();
  };

  /** `visibility: hidden` still has a box, so check it explicitly. */
  #isVisible() {
    if (typeof this.checkVisibility === 'function') {
      return this.checkVisibility({ visibilityProperty: true, contentVisibilityAuto: true });
    }
    const styles = getComputedStyle(this);
    return styles.visibility !== 'hidden' && styles.display !== 'none';
  }

  activateIfVisible() {
    if (this.#isVisible()) this.activate();
  }

  /** Attach the real sources the first time the panel opens, then play. */
  activate() {
    const video = this.video;
    if (!video) return;

    if (!RESOLVED.has(video)) {
      RESOLVED.add(video);

      const poster = video.dataset.poster;
      if (poster) video.poster = poster;

      for (const source of video.querySelectorAll('source[data-src]')) {
        source.src = source.dataset.src ?? '';
        source.removeAttribute('data-src');
      }
      video.load();
    }

    const played = video.play();
    if (played && typeof played.catch === 'function') {
      // Autoplay can be refused (e.g. data saver); the poster still shows.
      played.catch(() => {});
    }
  }

  deactivate() {
    this.video?.pause();
  }
}

if (!customElements.get('mega-panel-video')) {
  customElements.define('mega-panel-video', MegaPanelVideo);
}
